import { useMemo, useState } from "react";
import { TableHead } from "@/components/ui/table";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

// Sorts a list client-side. Default: id descending (newest first).
export function useSortableData(items, defaultKey = "id", defaultDir = "desc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
    const arr = [...(items || [])];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = get(a, sortKey);
      const bv = get(b, sortKey);
      const aEmpty = av == null || av === "";
      const bEmpty = bv == null || bv === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1; // empties always last
      if (bEmpty) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

// Clickable table header cell with a sort indicator.
export function SortableHead({ column, sort, className = "", right = false, children }) {
  const { sortKey, sortDir, toggleSort } = sort;
  const active = sortKey === column;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(column)}
        className={`inline-flex items-center gap-1 select-none cursor-pointer hover:text-slate-900 transition-colors ${right ? "flex-row-reverse" : ""} ${active ? "text-emerald-700 font-semibold" : ""}`}
        data-testid={`sort-${column}`}
      >
        <span>{children}</span>
        <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}
